// Wait for DOM to be ready before initializing the game
document.addEventListener("DOMContentLoaded", async function () {

  // ===== LOADING SCREEN MANAGEMENT =====
  const loadingScreen = document.getElementById("loading-screen");
  const loadingBarFill = document.getElementById("loading-bar-fill");
  const loadingText = document.querySelector(".loading-text");

  // ===== USERNAME SCREEN MANAGEMENT =====
  const usernameScreen = document.getElementById("username-screen");
  const usernameInput = document.getElementById("username-input");
  const emailInput = document.getElementById("email-input");
  const usernameSubmit = document.getElementById("username-submit");
  const usernameForm = document.querySelector(".username-form");
  const usernameSuggestions = document.getElementById("username-suggestions");
  const usernameProblem = document.getElementById("username-problem");

  function updateLoadingProgress(percent, text) {
    if (loadingBarFill && typeof percent === 'number') {
      loadingBarFill.style.width = Math.min(100, Math.max(0, percent)) + "%";
    }
    if (loadingText && text) {
      loadingText.textContent = text;
    }
  }

  function hideLoadingScreen() {
    if (loadingScreen) {
      loadingScreen.classList.add("hidden");
    }
  }

  // ===== USERNAME MANAGEMENT =====
  // Storage keys for user data persistence across sessions
  const USERNAME_STORAGE_KEY = "pot_game_username";
  const EMAIL_STORAGE_KEY = "pot_game_email";
  const USER_DATA_STORAGE_KEY = "pot_game_user_data";

  /**
   * Helper function to get stored value from localStorage or cookie fallback
   * @param {string} key - The storage key to retrieve
   * @returns {string|null} The stored value or null if not found
   */
  function getStoredValue(key) {
    // Try localStorage first
    const stored = localStorage.getItem(key);
    if (stored) return stored;

    // Fallback to cookie
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const [cookieKey, value] = cookie.trim().split("=");
      if (cookieKey === key) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  function getStoredUsername() {
    return getStoredValue(USERNAME_STORAGE_KEY);
  }

  function getStoredEmail() {
    return getStoredValue(EMAIL_STORAGE_KEY);
  }

  function getStoredUserData() {
    const stored = localStorage.getItem(USER_DATA_STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  /**
   * Store user data in both localStorage and cookies for persistence
   * @param {string} username - User's username
   * @param {string} email - User's email address
   * @param {Object|null} userData - Additional user data (optional)
   */
  function storeUserData(username, email, userData = null) {
    // Store individual values for quick access
    localStorage.setItem(USERNAME_STORAGE_KEY, username);
    localStorage.setItem(EMAIL_STORAGE_KEY, email);

    // Store complete user data object with timestamp
    const completeUserData = {
      username,
      email,
      userData,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(
      USER_DATA_STORAGE_KEY,
      JSON.stringify(completeUserData)
    );

    // Also store in cookies as backup (expires in 1 year)
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${USERNAME_STORAGE_KEY}=${encodeURIComponent(
      username
    )}; expires=${expires.toUTCString()}; path=/`;
    document.cookie = `${EMAIL_STORAGE_KEY}=${encodeURIComponent(
      email
    )}; expires=${expires.toUTCString()}; path=/`;
  }

  // Helper function to remove a cookie
  function removeCookie(key) {
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  function clearUserData() {
    // Remove from localStorage
    [USERNAME_STORAGE_KEY, EMAIL_STORAGE_KEY, USER_DATA_STORAGE_KEY].forEach(key => {
      localStorage.removeItem(key);
    });

    // Remove cookies
    removeCookie(USERNAME_STORAGE_KEY);
    removeCookie(EMAIL_STORAGE_KEY);
  }

  // Legacy function for backward compatibility
  function storeUsername(username) {
    const email = getStoredEmail();
    if (email) {
      storeUserData(username, email);
    } else {
      // Fallback to old method
      localStorage.setItem(USERNAME_STORAGE_KEY, username);
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = `${USERNAME_STORAGE_KEY}=${encodeURIComponent(
        username
      )}; expires=${expires.toUTCString()}; path=/`;
    }
  }

  function clearUsername() {
    clearUserData();
  }

  function clearUsernameAndRefresh() {
    // Show confirmation dialog
    const confirmed = confirm(
      "Are you sure you want to change your username? This will restart the game."
    );

    if (confirmed) {

      // Clear the username
      clearUsername();

      // Clear game progress (optional - you might want to keep this)
      localStorage.removeItem("clicks");

      // Refresh the page
      window.location.reload();
    }
  }

  function validateUsername(username) {
    if (!username || username.trim().length === 0) {
      return { valid: false, message: "Username cannot be empty" };
    }

    const trimmed = username.trim();
    if (trimmed.length < 2) {
      return {
        valid: false,
        message: "Username must be at least 2 characters",
      };
    }

    if (trimmed.length > 20) {
      return {
        valid: false,
        message: "Username must be 20 characters or less",
      };
    }

    // Check for valid characters (alphanumeric, spaces, hyphens, underscores)
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(trimmed)) {
      return {
        valid: false,
        message:
          "Username can only contain letters, numbers, spaces, hyphens, and underscores",
      };
    }

    return { valid: true, message: "Username is valid" };
  }

  // Enhanced client-side validation that matches API rules
  function validateUsernameStrict(username) {
    const basicValidation = validateUsername(username);
    if (!basicValidation.valid) {
      return basicValidation;
    }

    const trimmed = username.trim();

    // Check for numbers (API rejects usernames with numbers)
    if (/\d/.test(trimmed)) {
      return {
        valid: false,
        message: "Username contains numbers which may not be suitable",
        suggestions: ["CozyBear", "HibernationBuddy", "HoneyHugger"],
        problem: "Username contains numbers which may not be suitable",
      };
    }

    // Check for common inappropriate words
    const inappropriateWords = [
      "admin",
      "administrator",
      "root",
      "user",
      "guest",
      "test",
    ];
    if (inappropriateWords.includes(trimmed.toLowerCase())) {
      return {
        valid: false,
        message: "Username implies a role that may not be suitable",
        suggestions: ["CozyBear", "FriendlyBruin", "HibernationBuddy"],
        problem: "Username implies a role that may not be suitable",
      };
    }

    return { valid: true, message: "Username is valid" };
  }

  /**
   * Validate username through API - checks for appropriateness and availability
   * API validation is MANDATORY - no client-side fallbacks allowed
   * @param {string} username - Username to validate
   * @returns {Promise<Object>} Validation result with success flag, message, and suggestions
   */
  async function validateUsernameAPI(username) {
    const API_ENDPOINT =
      "https://k8szdy4kxa.execute-api.us-east-2.amazonaws.com/default/validateUserName";

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        body: JSON.stringify({ userName: username }),
      });

      if (!response.ok) {
        // Handle HTTP errors - NO FALLBACK TO CLIENT VALIDATION
        const errorData = await response.json().catch(() => ({}));

        // API validation is MANDATORY - no fallbacks allowed
        return {
          success: false,
          message:
            "Username validation service unavailable. Please try again later.",
          suggestions: [],
          problem: "Validation service unavailable",
          apiError: true,
        };
      }

      const data = await response.json();

      if (data.isValid) {
        return {
          success: true,
          message: "Username is available",
          suggestions: [],
        };
      } else {
        return {
          success: false,
          message: data.validation.problem || "Username is not appropriate",
          suggestions: data.validation.suggestions || [],
          problem: data.validation.problem,
        };
      }
    } catch (error) {
      console.error('Username validation API error:', error);
      // API validation is MANDATORY - no fallbacks allowed
      return {
        success: false,
        message:
          "Unable to validate username. Please check your connection and try again.",
        suggestions: [],
        problem: "Network error - unable to validate username",
        apiError: true,
      };
    }
  }

  // Email validation API - checks if email exists and returns user data
  async function validateEmailAPI(email) {
    const API_ENDPOINT =
      "https://kp3gh3q4qd.execute-api.us-east-2.amazonaws.com/default/getUsernamesByEmail";

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        return {
          success: false,
          message:
            "Email validation service unavailable. Please try again later.",
          apiError: true,
        };
      }

      const data = await response.json();

      // Update personal score with proper handling and retry mechanism
      const updatePersonalScore = (scoreValue) => {
        const curElement = document.querySelector("#cur");

        if (curElement) {
          curElement.textContent = Number(scoreValue).toLocaleString("en-US");
          return true;
        } else {
          return false;
        }
      };

      const scoreValue =
        data.score !== undefined && data.score !== null ? data.score : 0;

      // Update personal score immediately
      // Try to update immediately
      if (!updatePersonalScore(scoreValue)) {
        // If element not found, retry after a short delay
        setTimeout(() => {
          updatePersonalScore(scoreValue);
        }, 100);
      }

      // Store the score for window.currentUser initialization
      window.pendingPersonalScore = scoreValue;
      // Check if email was found
      if (data.message === "no email found") {
        // Clear any stored user data (cookies and localStorage) for new users
        clearUserData();
        
        return {
          success: true,
          isNewUser: true,
          message: "Email not found - new user",
          userData: null,
        };
      } else if (
        (data.username || data.userName) &&
        (data.email || data.emailEncrypted) &&
        data.score !== undefined
      ) {
        return {
          success: true,
          isNewUser: false,
          message: "Email found - existing user",
          userData: {
            username: data.username || data.userName,
            email: data.email || data.emailEncrypted,
            score: data.score,
          },
        };
      } else {
        return {
          success: false,
          message: "Invalid response from email validation service",
          apiError: true,
        };
      }
    } catch (error) {
      console.error('Email validation API error:', error);
      // Handle different types of errors
      if (error.name === "AbortError") {
        return {
          success: false,
          message: "Request timed out. Please try again.",
          apiError: true,
        };
      } else if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        return {
          success: false,
          message: "Network error. Please check your connection and try again.",
          apiError: true,
        };
      } else {
        return {
          success: false,
          message: "Unable to validate email. Please try again later.",
          apiError: true,
        };
      }
    }
  }

  // Tier status API - checks current tier unlock status
  async function getTierStatusAPI() {
    const TIER_STATUS_ENDPOINT =
      "https://wsqh5wrim1.execute-api.us-east-2.amazonaws.com/default/myPoTGameSettings";

    try {
      const response = await fetch(TIER_STATUS_ENDPOINT, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return data;
    } catch (error) {
      return null;
    }
  }

  // Score submission API - submits user's score after each click
  async function submitUserScore(username, email, score) {
    const API_ENDPOINT =
      "https://x1bjz1lvh0.execute-api.us-east-2.amazonaws.com/default/createUsernameByEmail";

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: "Bearer pot-game-secret-2024",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userName: username,
          email: email,
          score: score,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Score submission failed:', response.status, errorData);
        return {
          success: false,
          message: "Score submission failed. Please try again.",
          apiError: true,
        };
      }

      const data = await response.json();

      return {
        success: true,
        message: "Score submitted successfully",
        data: data,
      };
    } catch (error) {
      console.error('Score submission error:', error);
      return {
        success: false,
        message:
          "Unable to submit score. Please check your connection and try again.",
        apiError: true,
      };
    }
  }

  function showUsernameScreen() {
    if (usernameScreen) {
      usernameScreen.classList.remove("hidden");
    }
  }

  function hideUsernameScreen() {
    if (usernameScreen) {
      usernameScreen.classList.add("hidden");
    }
  }

  function setFormState(state) {
    if (usernameForm) {
      usernameForm.classList.remove("error", "success");
      if (state) {
        usernameForm.classList.add(state);
      }
    }
  }

  function showUsernameError(message) {
    setFormState("error");
    // You could add a message element here to show the error
  }

  function updateValidationStep(step, message) {
    if (usernameSubmit) {
      usernameSubmit.textContent = message;
    }
  }

  function showUsernameField() {
    if (usernameInput) {
      usernameInput.style.display = "block";
      usernameInput.focus();
    }
    if (usernameScreen) {
      const subtitle = usernameScreen.querySelector(".username-subtitle");
      if (subtitle) {
        subtitle.textContent = "Create Your Username";
      }
    }
    if (usernameSubmit) {
      usernameSubmit.textContent = "Create Account";
    }
  }

  function hideUsernameField() {
    if (usernameInput) {
      usernameInput.style.display = "none";
      usernameInput.value = "";
    }
    if (usernameScreen) {
      const subtitle = usernameScreen.querySelector(".username-subtitle");
      if (subtitle) {
        subtitle.textContent = "Enter Your Email";
      }
    }
    if (usernameSubmit) {
      usernameSubmit.textContent = "Check Email";
    }
  }

  function updateUsernameNote(message) {
    const noteElement = usernameScreen?.querySelector(".username-note");
    if (noteElement) {
      noteElement.textContent = message;
    }
  }

  function showUsernameSuccess() {
    setFormState("success");
    hideSuggestions();
    hideProblem();
  }

  function showProblem(message) {
    if (usernameProblem) {
      usernameProblem.textContent = message;
      usernameProblem.style.display = "block";
    }
  }

  function hideProblem() {
    if (usernameProblem) {
      usernameProblem.style.display = "none";
    }
  }

  function showSuggestions(suggestions) {
    if (!usernameSuggestions || !suggestions || suggestions.length === 0)
      return;

    usernameSuggestions.innerHTML = "";
    usernameSuggestions.style.display = "block";

    const title = document.createElement("div");
    title.className = "suggestions-title";
    title.textContent = "Suggested usernames:";
    usernameSuggestions.appendChild(title);

    suggestions.forEach((suggestion) => {
      const suggestionEl = document.createElement("div");
      suggestionEl.className = "suggestion-item";
      suggestionEl.textContent = suggestion;
      suggestionEl.addEventListener("click", () => {
        usernameInput.value = suggestion;
        usernameInput.focus();
        hideSuggestions();
        hideProblem();
        setFormState("success");
        // Auto-validate the selected suggestion
        debounceValidation(suggestion);
      });
      usernameSuggestions.appendChild(suggestionEl);
    });
  }

  function hideSuggestions() {
    if (usernameSuggestions) {
      usernameSuggestions.style.display = "none";
      usernameSuggestions.innerHTML = "";
    }
  }

  // Helper to delay execution
  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // ===== DEBOUNCE AND LIVE VALIDATION =====
  let debounceTimer = null;
  let isLiveValidating = false;

  function debounceValidation(username) {
    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Don't validate if username is too short or empty
    if (!username || username.trim().length < 3) {
      hideSuggestions();
      hideProblem();
      setFormState(null);
      return;
    }

    // Set new timer for debounced validation
    debounceTimer = setTimeout(async () => {
      await liveValidateUsername(username);
    }, 400); // 400ms debounce as recommended
  }

  async function liveValidateUsername(username) {
    if (isLiveValidating) return; // Prevent multiple simultaneous validations

    isLiveValidating = true;
    setFormState(null);
    hideProblem();

    try {
      const result = await validateUsernameAPI(username);

      if (result.success) {
        setFormState("success");
        hideSuggestions();
        hideProblem();
      } else {
        setFormState("error");
        if (result.suggestions && result.suggestions.length > 0) {
          showSuggestions(result.suggestions);
        } else {
          hideSuggestions();
        }
        if (result.problem) {
          showProblem(result.problem);
        } else {
          hideProblem();
        }
      }
    } catch (error) {
      setFormState(null);
      hideSuggestions();
      hideProblem();
    } finally {
      isLiveValidating = false;
    }
  }

  // ===== USERNAME EVENT HANDLERS =====
  async function handleUsernameSubmit() {
    const username = usernameInput.value.trim();
    const email = emailInput.value.trim();

    // Check if username field is visible (new user flow)
    const isUsernameVisible = usernameInput.style.display !== "none";

    // Basic validation based on current step
    if (!email) {
      showUsernameError("Please enter your email address.");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showUsernameError("Please enter a valid email address.");
      return;
    }

    // If username field is visible, validate username too
    if (isUsernameVisible && !username) {
      showUsernameError("Please enter a username.");
      return;
    }

    // Disable button during validation
    usernameSubmit.disabled = true;
    usernameSubmit.textContent = "Validating...";
    setFormState(null);
    hideSuggestions();
    hideProblem();

    try {
      // Check if this is the first step (email only) or second step (email + username)
      if (!isUsernameVisible) {
        // Step 1: Email validation to check if user exists
        updateValidationStep(1, "Checking email...");

        const emailResult = await validateEmailAPI(email);

        if (!emailResult.success || emailResult.apiError) {
          showUsernameError(emailResult.message);
          usernameSubmit.textContent = "Try Again";
          return;
        }

        // Process based on email validation result
        if (emailResult.isNewUser) {
          // Show username field for new user
          showUsernameField();
          updateUsernameNote("Please create a username for your new account");
          usernameSubmit.textContent = "Create Account";
          usernameSubmit.disabled = false;
          return; // Don't proceed to game yet
        } else {
          // For existing users, proceed directly to game
          updateValidationStep(2, "Welcome back!");
          storeUserData(
            emailResult.userData.username,
            email,
            emailResult.userData
          );
          showUsernameSuccess();

          // Personal score is already updated in validateEmailAPI function
          // Set window.currentUser for scoring system
          const initialScore =
            window.pendingPersonalScore !== undefined
              ? window.pendingPersonalScore
              : emailResult.userData.score;
          window.currentUser = {
            username: emailResult.userData.username,
            email: email,
            score: initialScore,
          };

          // Brief success state
          await delay(500);
          hideUsernameScreen();

          // Initialize game with existing user data
          await initializeGameWithUser(
            emailResult.userData.username,
            email,
            emailResult.userData.score
          );
        }
      } else {
        // Step 2: Username validation for new users
        updateValidationStep(2, "Validating username...");
        const usernameResult = await validateUsernameAPI(username);

        if (!usernameResult.success || usernameResult.apiError) {
          // Show error message
          if (usernameResult.apiError) {
            showUsernameError(
              "Username validation service is unavailable. Please try again later."
            );
          } else {
            showUsernameError(usernameResult.message);
          }

          // Show suggestions if available
          if (
            usernameResult.suggestions &&
            usernameResult.suggestions.length > 0
          ) {
            showSuggestions(usernameResult.suggestions);
          }

          // Show problem message if available
          if (usernameResult.problem) {
            showProblem(usernameResult.problem);
          }

          usernameSubmit.textContent = "Try Again";
          return;
        }

        // Username validation passed - proceed with new user
        updateValidationStep(3, "Creating new account...");
        storeUserData(username, email, null);
        showUsernameSuccess();

        // Brief success state
        await delay(500);
        hideUsernameScreen();

        // Personal score will be updated when validateEmailAPI is called for new users
        // Set window.currentUser for scoring system
        window.currentUser = {
          username: username,
          email: email,
          score: 0,
        };

        // Initialize game with new user (score starts at 0)
        await initializeGameWithUser(username, email, 0);
      }
    } catch (error) {
      showUsernameError("Validation failed. Please try again.");
      usernameSubmit.textContent = "Try Again";
    } finally {
      usernameSubmit.disabled = false;
    }
  }

  function handleUsernameInputKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleUsernameSubmit();
    }
  }

  // Set up username event listeners
  if (usernameSubmit) {
    usernameSubmit.addEventListener("click", handleUsernameSubmit);
  }

  if (usernameInput) {
    usernameInput.addEventListener("keydown", handleUsernameInputKeydown);

    // Live validation with debounce (only when username field is visible)
    usernameInput.addEventListener("input", (e) => {
      if (usernameInput.style.display !== "none") {
        debounceValidation(e.target.value);
      }
    });

    // Auto-focus the input when screen is shown
    usernameInput.addEventListener("focus", () => {
      setFormState(null);
      hideSuggestions();
      hideProblem();
    });

    // Hide suggestions when clicking outside
    document.addEventListener("click", (e) => {
      if (
        !usernameInput.contains(e.target) &&
        !usernameSuggestions?.contains(e.target)
      ) {
        hideSuggestions();
      }
    });
  }

  // ===== USER-SPECIFIC GAME INITIALIZATION =====
  /**
   * Initialize game with user-specific data
   * @param {string} username - User's username
   * @param {string} email - User's email
   * @param {number} initialScore - User's initial score from API
   */
  async function initializeGameWithUser(username, email, initialScore) {
    // Store current user data globally for use in scoring system
    const finalScore =
      window.pendingPersonalScore !== undefined
        ? window.pendingPersonalScore
        : initialScore;
    window.currentUser = {
      username: username,
      email: email,
      score: finalScore,
    };

    // Update username display in the welcome message
    updateUsernameDisplay(username);

    // Start loading
    updateLoadingProgress(0, "Initializing game...");
    await delay(200);
    updateLoadingProgress(10, "Loading user data...");

    // Continue with regular game initialization
    await initializeGame();

    // Check tier status after game initialization
    await checkAndUpdateTierStatus();

    // Start periodic tier status checking
    startTierStatusPolling();
  }

  /**
   * Update the username display in the welcome message
   * @param {string} username - Username to display
   */
  function updateUsernameDisplay(username) {
    const usernameElement = document.getElementById("username");
    if (usernameElement && username) {
      usernameElement.textContent = username;
    }
  }

  // ===== MAIN INITIALIZATION FLOW =====
  async function initializeGame() {
    // Start loading
    updateLoadingProgress(0, "Initializing game...");
    await delay(200);
    updateLoadingProgress(10, "Initializing game...");

    // ===== LAMBDA CONFIGURATION =====
    const LAMBDA_ENDPOINT =
      "https://wsqh5wrim1.execute-api.us-east-2.amazonaws.com/default/myPoTGameSettings"; // Replace with your actual endpoint

    // ===== LEADERBOARD API CONFIGURATION =====
    const LEADERBOARD_API_ENDPOINT =
      "https://gvyttn0zv9.execute-api.us-east-2.amazonaws.com/default/getLeaderBoardValue";

    // ===== TIER STATUS API CONFIGURATION =====
    const TIER_STATUS_ENDPOINT =
      "https://wsqh5wrim1.execute-api.us-east-2.amazonaws.com/default/myPoTGameSettings";

    // Game configuration - will be loaded from Lambda
    let gameConfig = null;

    // ===== LEADERBOARD API FUNCTIONS =====

    // Function to fetch leaderboard value from API
    async function fetchLeaderboardValue() {
      try {
        const response = await fetch(LEADERBOARD_API_ENDPOINT, {
          method: "GET",
          mode: "cors", // Explicitly set CORS mode
          headers: {
            Accept: "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          const result = data.leaderboard_value || 0;
          return result;
        } else {
          const errorText = await response
            .text()
            .catch(() => "Unable to read error response");
          console.error('Leaderboard API error:', response.status, errorText);
          return 0;
        }
      } catch (error) {
        console.error('Leaderboard fetch error:', error);
        // If CORS error, try using proxy approach
        if (
          error.name === "TypeError" &&
          (error.message.includes("CORS") ||
            error.message.includes("cross-origin"))
        ) {
          return await fetchLeaderboardValueFallback();
        }

        return 0;
      }
    }

    // Fallback method using CORS proxies
    async function fetchLeaderboardValueFallback() {
      const CORS_PROXY_ENDPOINTS = [
        "https://api.allorigins.win/raw?url=",
        "https://cors-anywhere.herokuapp.com/",
        "https://thingproxy.freeboard.io/fetch/",
      ];

      for (let i = 0; i < CORS_PROXY_ENDPOINTS.length; i++) {
        try {
          const proxyUrl =
            CORS_PROXY_ENDPOINTS[i] +
            encodeURIComponent(LEADERBOARD_API_ENDPOINT);

          const response = await fetch(proxyUrl, {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();
            return data.leaderboard_value || 0;
          }
        } catch (error) {
          continue;
        }
      }

      return 0;
    }

    // ===== FETCH CONFIGURATION FROM LAMBDA =====
    // Lambda configuration is MANDATORY - no fallbacks
    try {
      const response = await fetch(LAMBDA_ENDPOINT, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors", // Explicitly set CORS mode
        credentials: "omit", // Don't send cookies
      });

      if (response.ok) {
        const lambdaData = await response.json();

        if (lambdaData.success && lambdaData.data) {
          gameConfig = lambdaData.data;
        } else {
          throw new Error("Lambda response missing expected data structure");
        }
      } else {
        throw new Error(
          `Lambda request failed with status: ${response.status} ${response.statusText}`
        );
      }
    } catch (error) {
      console.error('Lambda configuration fetch error:', error);
      // Show error message to user
      showLambdaError(error);
      return; // Stop execution - game cannot run without Lambda config
    }

    // Configure UI elements with Lambda data
    configureUI();

    await delay(300);
    updateLoadingProgress(30, "Configuration loaded...");

    // Function to show Lambda error
    function showLambdaError(error) {
      // Hide loading screen
      if (loadingScreen) {
        loadingScreen.classList.add("hidden");
      }

      // Create error overlay
      const errorOverlay = document.createElement("div");
      errorOverlay.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: grid;
      place-items: center;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      font-family: Arial, sans-serif;
    `;

      errorOverlay.innerHTML = `
      <div style="
        background: #ff4444;
        border: 2px solid #ff6666;
        border-radius: 16px;
        padding: 32px;
        max-width: 500px;
        text-align: center;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
      ">
        <h2 style="margin: 0 0 16px; font-size: 24px; color: white;">
          🚨 Configuration Error
        </h2>
        <p style="margin: 0 0 16px; font-size: 16px; line-height: 1.5;">
          Failed to load game configuration from server.
        </p>
        <p style="margin: 0 0 24px; font-size: 14px; color: #ffcccc;">
          ${error.message}
        </p>
        <button onclick="location.reload()" style="
          background: #333;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
        ">
          🔄 Retry
        </button>
      </div>
    `;

      document.body.appendChild(errorOverlay);
    }

    // Function to configure UI elements with Lambda data
    function configureUI() {
      // Lambda data is mandatory - no fallbacks
      if (!gameConfig || !gameConfig.ui) {
        throw new Error("Missing UI configuration from Lambda");
      }

      // Update game title
      const gameElement = document.getElementById("game");
      if (gameElement) {
        gameElement.title = gameConfig.ui.gameTitle;
      }

      // Update page title if exists
      const titleElement = document.querySelector(
        "h1, .game-title, #game-title"
      );
      if (titleElement) {
        titleElement.textContent = gameConfig.ui.title;
      }

      // Update subtitle if exists
      const subtitleElement = document.querySelector(".subtitle, #subtitle");
      if (subtitleElement) {
        subtitleElement.textContent = gameConfig.ui.subtitle;
      }

      // Update description if exists
      const descriptionElement = document.querySelector(
        ".description, #description"
      );
      if (descriptionElement) {
        descriptionElement.textContent = gameConfig.ui.description;
      }

      // Update labels
      const goalElement = document.querySelector(".goal-label, #goal-label");
      if (goalElement) {
        goalElement.textContent = gameConfig.ui.goalLabel;
      }

      const currentElement = document.querySelector(
        ".current-label, #current-label"
      );
      if (currentElement) {
        currentElement.textContent = gameConfig.ui.currentLabel;
      }

      const remainingElement = document.querySelector(
        ".remaining-label, #remaining-label"
      );
      if (remainingElement) {
        remainingElement.textContent = gameConfig.ui.remainingLabel;
      }

      const progressElement = document.querySelector(
        ".progress-label, #progress-label"
      );
      if (progressElement) {
        progressElement.textContent = gameConfig.ui.progressLabel;
      }

      // Update button text - ensure leaderboard button shows "LEADERBOARD"
      const buttonElement = document.querySelector("#btn, .click-button");
      if (buttonElement) {
        // Keep the leaderboard button text as "LEADERBOARD" regardless of config
        if (buttonElement.id === "btn") {
          buttonElement.textContent = "LEADERBOARD";
        } else {
          buttonElement.textContent = gameConfig.ui.buttonText;
        }
      }

      // Update mute button text
      const muteButtonElement = document.getElementById("toggle-sound");
      if (muteButtonElement) {
        muteButtonElement.textContent = gameConfig.ui.muteButton;
      }
    }

    /***********************
     * Click Meter Logic   *
     ***********************/
    const TARGET = gameConfig.TARGET;
    const BONUS = gameConfig.BONUS;
    const fill = document.getElementById("fill");
    const cur = document.getElementById("cur");
    const rem = document.getElementById("rem");
    const pct = document.getElementById("pct");
    const btn = document.getElementById("btn");
    const panel = document.getElementById("panel");
    const meter = document.querySelector(".meter");
    const fmt = (n) => Number(n).toLocaleString("en-US");

    // Set up leaderboard button event listener immediately after button selection
    if (btn) {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        showLeaderboard();
      });
    }

    // Define leaderboard functions early so they're available for the event listener
    const gamePanel = document.getElementById("panel");
    const leaderboardPanel = document.getElementById("leaderboard-panel");
    const backToGameButton = document.getElementById("back-to-game");

    // Set up back button event listener immediately after button selection
    if (backToGameButton) {
      backToGameButton.addEventListener("click", (e) => {
        e.preventDefault();
        showGamePanel();
      });
    }

    // Function to show leaderboard
    function showLeaderboard() {
      if (gamePanel && leaderboardPanel) {
        gamePanel.classList.add("hidden");
        leaderboardPanel.classList.remove("hidden");

        // Load leaderboard data from API
        loadLeaderboardData();
      }
    }

    // Function to show game panel
    function showGamePanel() {
      if (gamePanel && leaderboardPanel) {
        leaderboardPanel.classList.add("hidden");
        gamePanel.classList.remove("hidden");
      }
    }

    // Function to load leaderboard data from API
    async function loadLeaderboardData() {
      // Show loading state
      showLeaderboardLoading();

      try {
        // Get current user info for ranking
        const currentUser = window.currentUser || {};
        const requestPayload = {
          email: currentUser.email || null,
          username: currentUser.username || null,
        };

        const API_ENDPOINT =
          "https://s4o7oxbjokq26tfociiy4qctay0umtzi.lambda-url.us-east-2.on.aws/"; // Temporary: using old endpoint until Function URL is fixed
        // Use GET request to avoid CORS preflight issues
        let response;

        // Build query string with user data
        const queryParams = new URLSearchParams();
        if (requestPayload.email)
          queryParams.append("email", requestPayload.email);
        if (requestPayload.username)
          queryParams.append("username", requestPayload.username);
        const queryString = queryParams.toString();
        const urlWithParams = queryString
          ? `${API_ENDPOINT}?${queryString}`
          : API_ENDPOINT;

        response = await fetch(urlWithParams, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer pot-game-secret-2024",
          },
          mode: "cors",
          credentials: "omit",
        });

        if (!response.ok) {
          throw new Error(`API request failed with status: ${response.status}`);
        }

        const apiData = await response.json();

        if (!apiData.success || !apiData.data) {
          throw new Error("Invalid API response format");
        }

        // Process API data for All Time leaderboard
        const allTimeData = apiData.data.topPlayers.map((player) => ({
          rank: player.rank,
          username: player.username,
          points: player.score,
          isCurrentUser: false,
        }));

        // Add current user rank if they're not in top 5
        let currentUserData = null;
        if (
          apiData.data.currentUserRank &&
          apiData.data.currentUserRank.rank > 5
        ) {
          currentUserData = {
            rank: apiData.data.currentUserRank.rank,
            username: apiData.data.currentUserRank.username,
            points: apiData.data.currentUserRank.score,
            isCurrentUser: true,
          };
        }

        // Update All Time leaderboard with real data
        updateLeaderboardSection(
          "alltime-leaderboard",
          allTimeData,
          currentUserData
        );

        // Process API data for Daily leaderboard
        const dailyData = apiData.data.dailyTopPlayers
          ? apiData.data.dailyTopPlayers.map((player) => ({
              rank: player.rank,
              username: player.username,
              points: player.score,
              isCurrentUser: false,
            }))
          : [];

        // Add current user daily rank if they're not in top 5
        let currentUserDailyData = null;
        if (
          apiData.data.currentUserDailyRank &&
          apiData.data.currentUserDailyRank.rank > 5
        ) {
          currentUserDailyData = {
            rank: apiData.data.currentUserDailyRank.rank,
            username: apiData.data.currentUserDailyRank.username,
            points: apiData.data.currentUserDailyRank.score,
            isCurrentUser: true,
          };
        }

        // Update Daily leaderboard with real data from API
        updateLeaderboardSection(
          "daily-leaderboard",
          dailyData,
          currentUserDailyData
        );

      } catch (error) {
        console.error('Leaderboard data load error:', error);
        // Show error state or fallback to mock data
        showLeaderboardError(error.message);

        // Try fallback with mock data that looks like real data
        const fallbackData = [
          { rank: 1, username: "sausages", points: 225300015 },
          { rank: 2, username: "CozyBearFriend", points: 21700166 },
          { rank: 3, username: "Ray", points: 10200035 },
          { rank: 4, username: "testFix", points: 4700010 },
          { rank: 5, username: "Bearfish", points: 4300027 },
        ];

        updateLeaderboardSection("alltime-leaderboard", fallbackData);
        updateLeaderboardSection("daily-leaderboard", fallbackData);
      }
    }

    // Function to show loading state
    function showLeaderboardLoading() {
      const loadingData = [
        { rank: 1, username: "Loading...", points: 0 },
        { rank: 2, username: "Loading...", points: 0 },
        { rank: 3, username: "Loading...", points: 0 },
        { rank: 4, username: "Loading...", points: 0 },
        { rank: 5, username: "Loading...", points: 0 },
      ];

      updateLeaderboardSection("alltime-leaderboard", loadingData);
    }

    // Function to show error state
    function showLeaderboardError(errorMessage) {
      // You could show a toast notification or error message here
    }

    // Function to update a leaderboard section
    function updateLeaderboardSection(sectionId, data, currentUserData = null) {
      const section = document.getElementById(sectionId);
      if (!section) return;

      section.innerHTML = "";

      // Add top 5 players
      data.forEach((player) => {
        const item = document.createElement("div");
        item.className = `leaderboard-item ${getRankClass(player.rank)}`;

        item.innerHTML = `
        <span class="rank">#${player.rank}</span>
        <span class="username">${player.username}</span>
        <span class="points">${player.points.toLocaleString()} points</span>
      `;

        section.appendChild(item);
      });

      // Add separator and current user if they're not in top 5
      if (currentUserData) {
        // Add separator
        const separator = document.createElement("div");
        separator.className = "leaderboard-separator";
        separator.innerHTML = "<span>...</span>";
        section.appendChild(separator);

        // Add current user
        const userItem = document.createElement("div");
        userItem.className = `leaderboard-item current-user ${getRankClass(
          currentUserData.rank
        )}`;

        userItem.innerHTML = `
        <span class="rank">#${currentUserData.rank}</span>
        <span class="username">${currentUserData.username}</span>
        <span class="points">${currentUserData.points.toLocaleString()} points</span>
      `;

        section.appendChild(userItem);
      }
    }

    // Function to get rank class for styling
    function getRankClass(rank) {
      if (rank === 1) return "rank-1";
      if (rank === 2) return "rank-2";
      if (rank === 3) return "rank-3";
      if (rank > 100) return "rank-other";
      return "";
    }

    // Initialize value from leaderboard API
    let value = 0;

    // Load initial value from leaderboard API
    try {
      const leaderboardValue = await fetchLeaderboardValue();
      value = leaderboardValue;
    } catch (error) {

      // Check if we have user data (from username/email validation)
      if (window.currentUser && window.currentUser.score !== undefined) {
        value = window.currentUser.score;
      } else {
        // Fallback to localStorage for backward compatibility
        value = +(localStorage.getItem("clicks") || 0);
      }
    }

    // Initial render will be called after user data is loaded

    function showCongrats() {
      if (document.getElementById("congrats")) return;
      const overlay = document.createElement("div");
      overlay.id = "congrats";
      Object.assign(overlay.style, {
        position: "fixed",
        inset: "0",
        zIndex: 9999,
        display: "grid",
        placeItems: "center",
        background: "rgb(255, 71, 19)",
        backdropFilter: "blur(3px)",
      });
      const card = document.createElement("div");
      Object.assign(card.style, {
        background: "#FF4713",
        border: "1px solid #2a3150",
        borderRadius: "16px",
        padding: "28px 32px",
        maxWidth: "max(200px,92vw)",
        textAlign: "center",
        color: "#e7ebff",
        fontFamily: "Ringside,Roboto,Arial",
      });
      // Use Lambda values only - no fallbacks
      const congratsTitle = gameConfig.ui.congratsTitle;
      const congratsMessage = gameConfig.ui.congratsMessage;
      const congratsDiscount = gameConfig.ui.congratsDiscount;
      const congratsButton = gameConfig.ui.congratsButton;

      card.innerHTML = `
      <h2 style="margin:0 0 10px;font-size:28px">${congratsTitle}</h2>
      <p style="margin:0 0 14px;font-size:18px">${congratsMessage}</p>
      <p style="margin:0 18px 18px;font-weight:800;font-size:22px">${congratsDiscount}</p>
      <button id="closeC" style="border:1px solid #2a3150;background:#1a1f3a;padding:12px 22px;border-radius:12px;color:#e7ebff;font-weight:800;cursor:pointer">${congratsButton}</button>
    `;
      overlay.appendChild(card);
      document.body.appendChild(overlay);
      card.animate(
        [
          { transform: "scale(.9)", opacity: 0 },
          { transform: "scale(1)", opacity: 1 },
        ],
        { duration: 300, easing: "ease-out" }
      );
      document.getElementById("closeC").onclick = () => {
        overlay.remove();
        onGameComplete(value); // Submit score when closing congrats
      };
    }

    function render() {
      if (!TARGET || TARGET <= 0) {
        console.error('Invalid TARGET value for render');
        return;
      }

      const p = Math.min(100, (value / TARGET) * 100);

      if (fill) fill.style.width = p + "%";

      // Personal score (cur) shows user's individual score, not the total leaderboard value
      let personalScore = window.currentUser ? window.currentUser.score : 0;

      // Check if there's a pending personal score from email validation (only use ONCE during initial load)
      if (window.pendingPersonalScore !== undefined && !window.currentUser) {
        personalScore = window.pendingPersonalScore;
        // Clear the pending score after using it ONCE
        delete window.pendingPersonalScore;
      }

      if (cur) cur.textContent = fmt(personalScore);
      if (rem) rem.textContent = fmt(Math.max(0, TARGET - value));
      if (pct) pct.textContent = p.toFixed(p >= 1 ? 2 : 0) + "%";
      if (meter) {
        meter.setAttribute("aria-valuenow", value);
      }
      // Don't disable the leaderboard button - it should always be accessible
      // btn.disabled = value >= TARGET;
      updateGoalDisplay(); // Update goal display with current value

      // Update fixed progress meter if it's visible
      updateFixedProgressMeter();

      if (value >= TARGET) showCongrats();
    }

    // Function to update the fixed progress meter
    function updateFixedProgressMeter() {
      const fixedFill = document.getElementById("fixed-fill");
      const fixedGoal = document.getElementById("fixed-goal");
      const fixedMeter = document.querySelector(".fixed-meter");

      if (fixedFill && fixedGoal && fixedMeter) {
        const p = Math.min(100, (value / TARGET) * 100);
        fixedFill.style.width = p + "%";
        fixedGoal.textContent = fmt(value);
        fixedMeter.setAttribute("aria-valuenow", value);
      }
    }

    // Function to update goal display with current API value
    function updateGoalDisplay() {
      const goalElement = document.getElementById("goal");
      if (goalElement) {
        goalElement.textContent = fmt(value);
      }
    }

    // Function to update tier display with unlock status and coupon codes
    function updateTierDisplay(tierStatus) {
      if (!tierStatus || !tierStatus.tiers) return;

      const levelItems = document.querySelectorAll(".level-item");
      const tierMapping = {
        0: "tier1", // Level 1: 250M points
        1: "tier2", // Level 2: 500M points
        2: "tier3", // Level 3: 750M points
        3: "tier4", // Level 4: 1B points
      };

      levelItems.forEach((item, index) => {
        const tierId = tierMapping[index];
        const tier = tierStatus.tiers[tierId];

        if (tier && tier.unlocked && tier.couponCode) {
          // Update the display to show the coupon code
          const strongElement = item.querySelector("strong");
          if (strongElement) {
            const levelText = strongElement.textContent;
            const restOfText = item.innerHTML.split("</strong>")[1];

            // Add unlocked styling and coupon code
            item.classList.add("tier-unlocked");
            item.innerHTML = `<strong>${levelText}</strong> UNLOCKED! ${restOfText} - Code: <span class="coupon-code">${tier.couponCode}</span>`;
          }
        }
      });
    }

    // Main function to check and update tier status
    async function checkAndUpdateTierStatus() {
      try {
        const tierStatus = await getTierStatusAPI();
        if (tierStatus) {
          updateTierDisplay(tierStatus);

          // Store in localStorage for quick access
          localStorage.setItem(
            "pot_game_tier_status",
            JSON.stringify(tierStatus)
          );
        }
      } catch (error) {
      }
    }

    // Set up periodic tier status checking (every 5 minutes)
    function startTierStatusPolling() {
      setInterval(async () => {
        await checkAndUpdateTierStatus();
      }, 5 * 60 * 1000); // 5 minutes
    }

    // Button click handler will be set up later with leaderboard functionality

    // Update progress bar with fresh leaderboard data every 10 seconds
    setInterval(async () => {
      try {
        const leaderboardValue = await fetchLeaderboardValue();

        // Only update if the value has changed significantly (to avoid unnecessary re-renders)
        if (Math.abs(leaderboardValue - value) > 0) {
          value = leaderboardValue;
          render();
        }
      } catch (error) {
        console.error('Leaderboard update error:', error);
      }

      // Call handleScores API
      try {
        const response = await fetch(
          "https://hzjs6n4vsj.execute-api.us-east-2.amazonaws.com/default/handleScores",
          {
            method: "POST",
            body: JSON.stringify({}),
          }
        );

        if (response.ok) {
          const data = await response.json();
        }
      } catch (error) {
        console.error('Handle scores API error:', error);
      }
    }, 10000); // Update every 10 seconds

    /***********************
     * Bear Hunt + Trees   *
     ***********************/
    const game = document.getElementById("game");
    const ctx = game.getContext("2d");
    game.classList.add("net");

    function sizeCanvas() {
      game.width = window.innerWidth;
      game.height = window.innerHeight;
    }
    sizeCanvas();
    window.addEventListener("resize", () => {
      sizeCanvas();
      layoutInitialTrees();
    });

    // ===== Sprites (32x32 pixel art) =====
    function makeCanvas(w = 32, h = 32) {
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      return c;
    }

    // Bear sprite
    const bearSprite = makeCanvas(32, 32);
    const bctx = bearSprite.getContext("2d");
    const fur = gameConfig.colors.fur,
      shade = gameConfig.colors.shade,
      ear = gameConfig.colors.ear,
      snout = gameConfig.colors.snout,
      mask = gameConfig.colors.mask,
      eyeWhite = gameConfig.colors.eyeWhite,
      pupil = gameConfig.colors.pupil,
      nose = gameConfig.colors.nose;

    (function drawBear() {
      // torso & shading
      bctx.fillStyle = fur;
      bctx.fillRect(6, 10, 20, 16);
      bctx.fillStyle = shade;
      bctx.fillRect(6, 18, 20, 8);

      // head
      bctx.fillStyle = fur;
      bctx.fillRect(8, 2, 16, 12);
      bctx.fillStyle = ear;
      bctx.fillRect(8, 1, 4, 3);
      bctx.fillRect(20, 1, 4, 3);

      // mask band (slightly taller)
      bctx.fillStyle = mask;
      bctx.fillRect(9, 6, 14, 5);

      // small eyes inside mask
      bctx.fillStyle = eyeWhite;
      bctx.fillRect(12, 7, 2, 2); // left white dot
      bctx.fillRect(18, 7, 2, 2); // right white dot

      // pupils (tiny black centers)
      bctx.fillStyle = pupil;
      bctx.fillRect(13, 8, 1, 1); // left pupil
      bctx.fillRect(19, 8, 1, 1); // right pupil

      // snout + nose
      bctx.fillStyle = snout;
      bctx.fillRect(14, 9, 4, 3);
      bctx.fillStyle = nose;
      bctx.fillRect(16, 10, 1, 1);

      // arms & legs
      bctx.fillStyle = fur;
      bctx.fillRect(4, 12, 4, 8);
      bctx.fillRect(24, 12, 4, 8);
      bctx.fillRect(10, 26, 4, 4);
      bctx.fillRect(18, 26, 4, 4);
    })();

    // Pine tree sprite
    const treeSprite = makeCanvas(32, 32);
    const tctx = treeSprite.getContext("2d");
    (function drawTree() {
      // trunk
      tctx.fillStyle = gameConfig.colors.trunk;
      tctx.fillRect(14, 22, 4, 8);
      // layered pine boughs
      const greens = gameConfig.colors.greens;
      function tri(y, w, c) {
        tctx.fillStyle = c;
        const x = 16 - w / 2;
        tctx.fillRect(x, y, w, 4);
      }
      tri(6, 8, greens[0]);
      tri(10, 14, greens[1]);
      tri(14, 20, greens[0]);
      tri(18, 24, greens[2]);
    })();

    // Scale for visibility
    let scale = gameConfig.scale;

    // 🐻 Keep bear at normal scale
    let bearW = bearSprite.width * scale;
    let bearH = bearSprite.height * scale;

    // 🌲 Make trees 1.5× larger than normal
    const TREE_SCALE_MULTIPLIER = 1.3; // adjust to taste (1.2–2.0 looks good)
    let treeW = treeSprite.width * scale * TREE_SCALE_MULTIPLIER;
    let treeH = treeSprite.height * scale * TREE_SCALE_MULTIPLIER;

    // ===== World objects =====
    // 🌲 Adjust tree density based on screen size
    let TREE_MIN = gameConfig.TREE_MIN;
    let TREE_MAX = gameConfig.TREE_MAX;

    const area = window.innerWidth * window.innerHeight;

    // Scale down trees on smaller screens
    if (area < 600_000) {
      // small phone (~<800×750)
      TREE_MIN = Math.floor(TREE_MIN * 0.1);
      TREE_MAX = Math.floor(TREE_MAX * 0.3);
    } else if (area < 1_000_000) {
      // large phone / small tablet
      TREE_MIN = Math.floor(TREE_MIN * 0.6);
      TREE_MAX = Math.floor(TREE_MAX * 0.7);
    } else if (area < 1_600_000) {
      // mid tablet
      TREE_MIN = Math.floor(TREE_MIN * 0.8);
      TREE_MAX = Math.floor(TREE_MAX * 0.9);
    }
    const HIT_PAD = gameConfig.HIT_PAD;
    const trees = []; // {x,y,alpha,state}

    let isPanelVisible = true;

    function panelRect() {
      if (!isPanelVisible) return { x: -9999, y: -9999, w: 0, h: 0 }; // Panel is hidden, return off-screen rect
      const r = panel.getBoundingClientRect();
      return { x: r.left, y: r.top, w: r.width, h: r.height };
    }
    function rectsOverlap(ax, ay, aw, ah, bx, by, bw, bh) {
      return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
    }
    function farFromPanel(x, y, w, h) {
      if (!isPanelVisible) return true; // Panel is hidden, everything is far from it
      const r = panelRect();
      return !rectsOverlap(x, y, w, h, r.x, r.y, r.w, r.h);
    }
    function spaced(x, y) {
      return trees.every(
        (t) =>
          Math.hypot(
            t.x + treeW / 2 - (x + treeW / 2),
            t.y + treeH / 2 - (y + treeH / 2)
          ) >
          treeW * 0.9
      );
    }

    function spawnTree() {
      let tries = 120;
      while (tries--) {
        const isMobile = window.innerWidth < 768; // detect mobile
        const progressBar = document.getElementById("fixed-progress-meter"); // optional: your progress bar element
        const topMargin = isMobile
          ? progressBar
            ? progressBar.clientHeight + 10
            : 120 // 120px fallback
          : 0;

        const x = Math.random() * (game.width - treeW);
        const y = topMargin + Math.random() * (game.height - topMargin - treeH);

        if (!farFromPanel(x, y, treeW, treeH)) continue;
        if (!spaced(x, y)) continue;

        trees.push({ x, y, alpha: 0, state: "in" }); // fade in
        return true;
      }
      return false;
    }

    function despawnTree() {
      if (trees.length <= TREE_MIN) return false;
      // don't despawn the tree we are hiding behind or currently targeting
      const safeIdx = new Set([hideAtIdx, targetIdx]);
      const candidates = trees.map((t, i) => i).filter((i) => !safeIdx.has(i));
      if (candidates.length === 0) return false;
      const i = candidates[(Math.random() * candidates.length) | 0];
      if (i === hideAtIdx) return false; // extra guard
      trees[i].state = "out"; // fade out and remove later
      return true;
    }

    function layoutInitialTrees() {
      trees.length = 0;
      for (let i = 0; i < TREE_MIN; i++) spawnTree();
    }

    await delay(300);
    updateLoadingProgress(50, "Spawning forest...");
    layoutInitialTrees();
    await delay(400);
    updateLoadingProgress(70, "Growing trees...");

    // ===== BEAR AI: Dynamic movement system =====
    // Bear runs from tree to tree with randomized speed and occasional direction changes
    const SPEED_BASE = gameConfig.SPEED_BASE;
    const SPEED_VAR = gameConfig.SPEED_VAR;
    const JITTER_MAX = gameConfig.JITTER_MAX;
    let speed = SPEED_BASE + Math.random() * SPEED_VAR;
    
    // Bear position and velocity
    let bx = Math.random() * (game.width - bearW);
    let by = Math.random() * (game.height - bearH);
    let vx = 0,
      vy = 0;

    // AI state variables for tree targeting and hiding behavior
    let targetIdx = -1;        // Index of tree bear is moving toward
    let hideTimer = 0;         // Frames remaining while bear is hiding
    let hideAtIdx = -1;        // Index of tree bear is hiding behind
    let retargetCooldown = 0;  // Prevents too frequent direction changes
    function pickTarget() {
      if (trees.length === 0) {
        targetIdx = -1;
        return;
      }

      // Filter out trees that are in or very close to the panel area (with padding)
      const r = panelRect();
      const padding = treeW; // Add padding to avoid trees near panel edges
      const safeChoices = trees
        .map((_, i) => i)
        .filter((i) => {
          if (i === targetIdx) return false; // Don't pick current target
          if (!isPanelVisible) return true; // All trees are valid if panel is hidden

          const t = trees[i];
          // Check if tree overlaps with panel area (with padding)
          return !rectsOverlap(
            t.x,
            t.y,
            treeW,
            treeH,
            r.x - padding,
            r.y - padding,
            r.w + padding * 2,
            r.h + padding * 2
          );
        });

      // If we have safe choices, pick from them; otherwise pick any tree except current
      if (safeChoices.length > 0) {
        targetIdx = safeChoices[(Math.random() * safeChoices.length) | 0];
      } else {
        const choices = trees.map((_, i) => i).filter((i) => i !== targetIdx);
        targetIdx = choices[(Math.random() * choices.length) | 0] || -1;
      }
    }
    pickTarget();

    function steer() {
      // if target vanished, pick another
      if (targetIdx < 0 || !trees[targetIdx]) pickTarget();
      if (targetIdx < 0) return;

      const tx = trees[targetIdx].x + treeW / 2;
      const ty = trees[targetIdx].y + treeH / 2;
      const cx = bx + bearW / 2;
      const cy = by + bearH / 2;
      let dx = tx - cx;
      let dy = ty - cy;
      const dist = Math.hypot(dx, dy) || 1;
      dx /= dist;
      dy /= dist; // normalized

      // add lateral randomness (wobble)
      const jitterX = (Math.random() - 0.5) * JITTER_MAX;
      const jitterY = (Math.random() - 0.5) * JITTER_MAX;
      let jx = dx + jitterX,
        jy = dy + jitterY;
      const jlen = Math.hypot(jx, jy) || 1;
      jx /= jlen;
      jy /= jlen;

      vx = jx * speed;
      vy = jy * speed;

      // occasionally mid-run pick a different tree for unpredictability
      if (retargetCooldown <= 0 && Math.random() < gameConfig.retargetChance) {
        pickTarget();
        retargetCooldown = gameConfig.retargetCooldown;
      } else {
        retargetCooldown = Math.max(0, retargetCooldown - 1);
      }

      // arrived near tree -> hide briefly, then dash to another
      if (dist < treeW * 0.35) {
        hideTimer =
          (gameConfig.hideTimerBase +
            Math.random() * gameConfig.hideTimerVar * 4) |
          0;
        hideAtIdx = targetIdx;
        speed = SPEED_BASE + Math.random() * SPEED_VAR; // vary speed for next run
        pickTarget();
      }
    }

    let frame = 0;
    function loop() {
      ctx.clearRect(0, 0, game.width, game.height);

      // Evolving forest: random spawns/despawns
      if (frame % 6 === 0) {
        // tick ~10x/sec on 60fps
        if (trees.length < TREE_MIN) {
          spawnTree();
        } else {
          const spawnChance =
            trees.length < TREE_MAX ? gameConfig.spawnChance : 0.0;
          const despawnChance =
            trees.length > TREE_MIN ? gameConfig.despawnChance : 0.0;
          if (Math.random() < spawnChance) spawnTree();
          if (Math.random() < despawnChance) despawnTree();
        }
      }

      // Update tree fades & cull fully hidden
      for (let i = trees.length - 1; i >= 0; i--) {
        const t = trees[i];
        if (t.state === "in") {
          t.alpha = Math.min(1, t.alpha + 0.06);
          if (t.alpha >= 1) t.state = "live";
        } else if (t.state === "out") {
          t.alpha = Math.max(0, t.alpha - 0.06);
          if (t.alpha <= 0) {
            trees.splice(i, 1);
            if (hideAtIdx === i) {
              hideTimer = 0;
              hideAtIdx = -1;
            }
            if (targetIdx === i) {
              pickTarget();
            }
            continue;
          }
        }
      }

      // Move logic
      if (hideTimer > 0) {
        hideTimer--;
      } else {
        steer();
        bx += vx;
        by += vy;
      }

      // Bounce off edges
      if (bx <= 0 || bx + bearW >= game.width) {
        vx *= -1;
        bx = Math.max(0, Math.min(bx, game.width - bearW));
      }
      if (by <= 0 || by + bearH >= game.height) {
        vy *= -1;
        by = Math.max(0, Math.min(by, game.height - bearH));
      }

      // Draw bear first (so trees can cover it while hiding)
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(bearSprite, Math.floor(bx), Math.floor(by), bearW, bearH);

      // Draw trees (foreground) with fade alpha
      for (let i = 0; i < trees.length; i++) {
        const t = trees[i];
        ctx.save();
        ctx.globalAlpha = t.alpha == null ? 1 : t.alpha;
        ctx.drawImage(
          treeSprite,
          Math.floor(t.x),
          Math.floor(t.y),
          treeW,
          treeH
        );
        if (hideTimer > 0 && i === hideAtIdx) {
          ctx.globalAlpha *= 0.35;
          ctx.fillStyle = "#7ed0ff";
          ctx.fillRect(Math.floor(t.x) + 12, Math.floor(t.y) + 6, 8, 3);
        }
        ctx.restore();
      }

      // Draw floating items and particles
      drawItem();

      frame++;
      requestAnimationFrame(loop);
    }

    await delay(300);
    updateLoadingProgress(90, "Preparing game loop...");
    requestAnimationFrame(loop);

    // ===== USER SCORE MANAGEMENT =====
    async function submitUserScoreToAPI(scoreIncrement) {
      if (!window.currentUser) {
        return false;
      }

      const newScore = window.currentUser.score + scoreIncrement;

      try {
        const result = await submitUserScore(
          window.currentUser.username,
          window.currentUser.email,
          newScore
        );

        if (result.success) {
          // Update local user data
          window.currentUser.score = newScore;
          return true;
        } else {
          return false;
        }
      } catch (error) {
        return false;
      }
    }

    // ===== CAPTURE LOGIC =====
    /**
     * Grant bonus points when player successfully captures the bear
     * @param {number} clickX - X coordinate of click
     * @param {number} clickY - Y coordinate of click
     * @returns {Promise<boolean>} True if bonus was granted successfully
     */
    async function grantBonus(clickX, clickY) {
      if (value >= TARGET) return false;

      const scoreIncrement = BONUS;
      const newValue = Math.min(TARGET, value + scoreIncrement);

      // Submit score to API if user is logged in
      if (window.currentUser) {
        const success = await submitUserScoreToAPI(scoreIncrement);
        if (success) {
          // Don't update the progress bar value (value) - it should only come from leaderboard API
          // Just re-render to update the personal score display (cur element)
          render();
        } else {
          return false;
        }
      } else {
        // Fallback for non-logged-in users - update localStorage but not progress bar
        localStorage.setItem(
          "clicks",
          window.currentUser ? window.currentUser.score : 0
        );
        render();
      }

      // Random bear growl (10% chance)
      if (Math.random() < 0.1) {
        playSfx("sfx-bear");
      }

      const pop = document.createElement("div");
      pop.className = "pop";
      pop.textContent = `+${BONUS.toLocaleString("en-US")}`;
      pop.style.left = clickX - 10 + "px";
      pop.style.top = clickY - 10 + "px";
      document.body.appendChild(pop);
      pop.animate(
        [
          { transform: "translateY(0)", opacity: 1 },
          { transform: "translateY(-40px)", opacity: 0 },
        ],
        { duration: 2000, easing: "ease-out" }
      ).onfinish = () => pop.remove();

      // Speed up after capture for extra challenge
      speed += 0.8;
      const burst = 1.18;
      vx *= burst;
      vy *= burst;
      // break hide if we were hiding
      hideTimer = 0;
      hideAtIdx = -1;
      return true;
    }

    function pointInRect(px, py, x, y, w, h) {
      return px >= x && px <= x + w && py >= y && py <= y + h;
    }
    function hitBear(x, y) {
      return pointInRect(
        x,
        y,
        bx - HIT_PAD,
        by - HIT_PAD,
        bearW + HIT_PAD * 2,
        bearH + HIT_PAD * 2
      );
    }

    // Accessibility: spacebar capture (works only if not hiding)
    window.addEventListener("keydown", (e) => {
      if (e.code === "Space") {
        const r = panelRect();
        const cx = bx + bearW / 2,
          cy = by + bearH / 2;
        const hiddenByPanel =
          isPanelVisible &&
          cx > r.x &&
          cx < r.x + r.w &&
          cy > r.y &&
          cy < r.y + r.h;
        if (!hiddenByPanel && hideTimer <= 0) {
          grantBonus(cx, cy);
        }
      }
    });

    // Set game title from Lambda only
    game.title = gameConfig.ui.gameTitle;

    /***********************
     * Floating Bonus Items
     ***********************/
    const ITEM_BONUS = gameConfig.ITEM_BONUS;
    let floatingItem = null;
    const itemScale = gameConfig.itemScale;

    // Utility: create a sprite
    function makeSprite(drawFn) {
      const c = makeCanvas(32, 32);
      const ctx = c.getContext("2d");
      drawFn(ctx);
      return c;
    }

    // === UNDERWEAR STYLES ===
    const briefsSprites = gameConfig.briefsStyles.map((style) =>
      makeSprite((ctx) => {
        ctx.fillStyle = style.waistband;
        ctx.fillRect(4, 2, 24, 4); // waistband
        ctx.fillStyle = style.body;
        ctx.fillRect(4, 6, 24, 12); // body
        ctx.fillRect(4, 18, 10, 6);
        ctx.fillRect(18, 18, 10, 6); // legs
      })
    );

    // === SOCK STYLES ===
    const socksSprites = gameConfig.socksStyles.map((style) =>
      makeSprite((ctx) => {
        ctx.fillStyle = style.primary;
        ctx.fillRect(12, 2, 6, 16);
        ctx.fillStyle = style.stripe1;
        ctx.fillRect(12, 2, 6, 2);
        ctx.fillStyle = style.stripe2;
        ctx.fillRect(12, 4, 6, 2);
        ctx.fillStyle = style.primary;
        ctx.fillRect(12, 6, 6, 2);
        ctx.fillStyle = style.stripe1;
        ctx.fillRect(12, 8, 6, 2);
        ctx.fillStyle = style.foot;
        ctx.fillRect(6, 16, 12, 6);
        ctx.fillStyle = style.heel;
        ctx.fillRect(6, 20, 6, 4);
      })
    );

    // === SPAWN LOGIC ===
    let nextIsBriefs = true;
    function spawnItem() {
      const type = nextIsBriefs ? "briefs" : "socks";
      nextIsBriefs = !nextIsBriefs;

      const spriteSet = type === "briefs" ? briefsSprites : socksSprites;
      const sprite = spriteSet[Math.floor(Math.random() * spriteSet.length)];

      let tries = 50;
      while (tries-- > 0) {
        const x = Math.random() * (game.width - 32 * itemScale);
        const y = Math.random() * (game.height - 32 * itemScale);

        if (!farFromPanel(x, y, 32 * itemScale, 32 * itemScale)) continue;

        // Gold splash colors
        const goldColors = gameConfig.colors.goldColors;

        floatingItem = {
          x,
          y,
          type,
          sprite,
          expires: performance.now() + gameConfig.itemLifetime,
          spawnTime: performance.now(),
        };

        // 💥 Spawn particles centered on the item (not corner)
        const centerX = x + (32 * itemScale) / 2;
        const centerY = y + (32 * itemScale) / 2;
        spawnParticles(centerX, centerY, goldColors);

        return;
      }
      floatingItem = null;
    }

    // Draw floating item + hover effect
    function drawItem() {
      if (!floatingItem) return;

      const now = performance.now();
      const age = now - floatingItem.spawnTime;
      const ttl = floatingItem.expires - now;
      const hoverOffset = Math.sin(age / 200) * 5;

      ctx.save();
      ctx.globalAlpha = ttl < 300 ? ttl / 300 : 1; // only fade on expire
      ctx.drawImage(
        floatingItem.sprite,
        floatingItem.x,
        floatingItem.y + hoverOffset,
        32 * itemScale,
        32 * itemScale
      );
      ctx.restore();

      if (now > floatingItem.expires) {
        floatingItem = null;
      }

      drawParticles();
    }

    // Check click collision with item
    function hitItem(x, y) {
      if (!floatingItem) return false;
      return pointInRect(
        x,
        y,
        floatingItem.x,
        floatingItem.y,
        32 * itemScale,
        32 * itemScale
      );
    }

    // Particle system for item spawns
    const particles = [];

    function spawnParticles(x, y, colors) {
      for (let i = 0; i < 8; i++) {
        particles.push({
          x: x,
          y: y,
          vx: (Math.random() - 0.5) * 8,
          vy: (Math.random() - 0.5) * 8,
          color: colors[Math.floor(Math.random() * colors.length)],
          life: 1.0,
          decay: 0.02,
        });
      }
    }

    function drawParticles() {
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life -= p.decay;

        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x - 2, p.y - 2, 4, 4);
        ctx.restore();
      }
    }

    // Consolidated click handler for both bear and items
    game.addEventListener("click", async (e) => {
      const rect = game.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Check for floating item first (higher priority)
      if (hitItem(x, y)) {
        const scoreIncrement = ITEM_BONUS;
        const newValue = Math.min(TARGET, value + scoreIncrement);

        // Show immediate visual feedback
        if (floatingItem.type === "briefs") {
          playSfx("sfx-briefs");
        } else if (floatingItem.type === "socks") {
          playSfx("sfx-socks");
        }

        const pop = document.createElement("div");
        pop.className = "pop";
        pop.textContent = `+${ITEM_BONUS.toLocaleString("en-US")}`;
        pop.style.left = e.clientX - 10 + "px";
        pop.style.top = e.clientY - 10 + "px";
        document.body.appendChild(pop);
        pop.animate(
          [
            { transform: "translateY(0)", opacity: 1 },
            { transform: "translateY(-40px)", opacity: 0 },
          ],
          { duration: 2000, easing: "ease-out" }
        ).onfinish = () => pop.remove();

        floatingItem = null;

        // Submit score to API asynchronously (non-blocking)
        if (window.currentUser) {
          submitUserScoreToAPI(scoreIncrement).then((success) => {
            if (success) {
              // Don't update the progress bar value (value) - it should only come from leaderboard API
              // Just re-render to update the personal score display (cur element)
              render();
            }
          }).catch((error) => {
            console.error("Failed to submit score:", error);
          });
        } else {
          // Fallback for non-logged-in users - update localStorage but not progress bar
          localStorage.setItem(
            "clicks",
            window.currentUser ? window.currentUser.score : 0
          );
          render();
        }

        return; // Don't check for bear if we hit an item
      }

      // ---- Check for bear ----
      if (typeof hideTimer !== "undefined" && hideTimer > 0) {
        // Bear is hiding — block all clicks on it
        if (typeof hideAtIdx !== "undefined" && hideAtIdx >= 0) {
          const t = trees[hideAtIdx];
          if (t && pointInRect(x, y, t.x, t.y, treeW, treeH)) {
            // (Optional) play a rustling sound or visual cue
          }
        }
        return; // ❌ don't let clicks reach the bear while hiding
      }

      // Normal bear click detection
      if (hitBear(x, y)) {
        grantBonus(e.clientX, e.clientY);
      }
    });

    // spawn items based on config
    setInterval(() => {
      if (!floatingItem && Math.random() < gameConfig.itemSpawnChance) {
        spawnItem();
      }
    }, gameConfig.itemSpawnInterval);

    function playSfx(id) {
      const el = document.getElementById(id);
      if (!el) return;
      el.currentTime = 0; // rewind so it can replay quickly
      el.play().catch(() => {}); // Silently handle autoplay restrictions
    }

    const bgMusic = document.getElementById("bg-music");
    const toggleBtn = document.getElementById("toggle-sound");

    // Autoplay handling: wait for user interaction
    function tryPlayMusic() {
      if (bgMusic) {
        bgMusic.volume = 0.3; // softer default
        bgMusic.play().catch(() => {});
      }
    }
    window.addEventListener("click", tryPlayMusic, { once: true });
    window.addEventListener("keydown", tryPlayMusic, { once: true });

    // Toggle mute/unmute for background music only
    let isMuted = false;

    if (toggleBtn && bgMusic) {
      toggleBtn.addEventListener("click", () => {
        isMuted = !isMuted;
        bgMusic.muted = isMuted;
        // Use Lambda values only for mute buttons
        const muteButton = gameConfig.ui.muteButton;
        const unmuteButton = gameConfig.ui.unmuteButton;
        toggleBtn.textContent = isMuted ? unmuteButton : muteButton;
      });
    }

    // ===== LAMBDA INTEGRATION FUNCTIONS =====

    // Function to submit score to Lambda
    async function submitScore(score) {
      // Lambda is mandatory - always submit scores
      try {
        const response = await fetch(LAMBDA_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          mode: "cors",
          credentials: "omit",
          body: JSON.stringify({
            action: "submit_score",
            score: score,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
          }),
        });

        if (response.ok) {
          const result = await response.json();
        } else {
          const errorText = await response.text();
          console.error('Score submission to Lambda failed:', response.status, errorText);
        }
      } catch (error) {
        console.error('Score submission to Lambda error:', error);
      }
    }

    // Function to get leaderboard from Lambda
    async function getLeaderboard() {
      // Lambda is mandatory - always get leaderboard
      try {
        const response = await fetch(LAMBDA_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "get_leaderboard",
          }),
        });

        if (response.ok) {
          const result = await response.json();
          return result.data || [];
        }
      } catch (error) {
        console.error('Get leaderboard error:', error);
      }

      return [];
    }

    // Submit score when game is completed
    function onGameComplete(finalScore) {
      submitScore(finalScore);
      // You can also trigger other completion events here
    }

    // Simple CORS test function
    async function testCORSDirectly() {
      const API_ENDPOINT =
        "https://k8szdy4kxa.execute-api.us-east-2.amazonaws.com/default/validateUserName";

      try {
        const response = await fetch(API_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          mode: "cors",
          credentials: "omit",
          body: JSON.stringify({ userName: "test" }),
        });

        const data = await response.json();
        return { success: true, data };
      } catch (error) {
        console.error('CORS test error:', error);
        return { success: false, error };
      }
    }

    // Test suggestions display
    async function testSuggestions() {
      // Test with a username that should have suggestions
      const testUsername = "admin";

      try {
        const result = await validateUsernameAPI(testUsername);

        if (result.suggestions && result.suggestions.length > 0) {
          // Test displaying suggestions
          showSuggestions(result.suggestions);
          return { success: true, suggestions: result.suggestions };
        } else {
          return { success: false, message: "No suggestions returned" };
        }
      } catch (error) {
        console.error('Test suggestions error:', error);
        return { success: false, error };
      }
    }

    // Test security - ensure inappropriate usernames are blocked
    async function testSecurity() {
      const inappropriateUsernames = ["NaziBear", "admin", "bear123"];

      for (const username of inappropriateUsernames) {
        try {
          const result = await validateUsernameAPI(username);
          if (result.success) {
            return {
              success: false,
              message: `Security breach: ${username} was accepted`,
            };
          }
        } catch (error) {
          console.error('Security test error:', error);
        }
      }

      return { success: true, message: "Security tests passed" };
    }

    // Expose functions globally for debugging
    window.potGameAPI = {
      submitScore,
      getLeaderboard,
      gameConfig,
      currentScore: () => value,
      testUsernameAPI: validateUsernameAPI,
      validateUsername: validateUsername,
      validateUsernameStrict: validateUsernameStrict,
      testLiveValidation: liveValidateUsername,
      testSubmit: handleUsernameSubmit,
      clearUsername: clearUsername,
      clearUsernameAndRefresh: clearUsernameAndRefresh,
      getStoredUsername: getStoredUsername,
      testCORS: testCORSDirectly,
      testSuggestions: testSuggestions,
      testSecurity: testSecurity,
    };

    // ===== TOGGLE PANEL FUNCTIONALITY =====
    const togglePanelBtn = document.getElementById("toggle-panel");
    const fixedProgressMeter = document.getElementById("fixed-progress-meter");

    if (togglePanelBtn) {
      togglePanelBtn.addEventListener("click", () => {
        isPanelVisible = !isPanelVisible;
        if (isPanelVisible) {
          // Show the game panel (and hide leaderboard if it was open)
          const leaderboardPanel = document.getElementById("leaderboard-panel");
          if (
            leaderboardPanel &&
            !leaderboardPanel.classList.contains("hidden")
          ) {
            // If leaderboard was open, close it and show game panel
            leaderboardPanel.classList.add("hidden");
            panel.classList.remove("hidden");
          } else {
            // Normal case - just show the panel
            panel.classList.remove("hidden");
          }

          togglePanelBtn.textContent = "Hide Menu";
          // Hide fixed progress meter when panel is shown
          if (fixedProgressMeter) {
            fixedProgressMeter.classList.add("hidden");
          }
          // When panel is shown, remove any trees that are now in the panel area
          const r = panelRect();
          for (let i = trees.length - 1; i >= 0; i--) {
            const t = trees[i];
            if (rectsOverlap(t.x, t.y, treeW, treeH, r.x, r.y, r.w, r.h)) {
              // If this is the target or hide tree, pick a new one
              if (i === targetIdx) pickTarget();
              if (i === hideAtIdx) {
                hideTimer = 0;
                hideAtIdx = -1;
              }
              trees.splice(i, 1);
            }
          }
        } else {
          // Hide both game panel and leaderboard panel
          panel.classList.add("hidden");
          const leaderboardPanel = document.getElementById("leaderboard-panel");
          if (leaderboardPanel) {
            leaderboardPanel.classList.add("hidden");
          }

          togglePanelBtn.textContent = "Show Menu";
          // Show fixed progress meter when panel is hidden
          if (fixedProgressMeter) {
            fixedProgressMeter.classList.remove("hidden");
            // Update the fixed progress meter with current values
            updateFixedProgressMeter();
          }
          // When panel is hidden, allow trees to spawn in that area
          layoutInitialTrees();
        }
      });
    }

    // ===== CLEAR USERNAME FUNCTIONALITY =====
    const clearUsernameBtn = document.getElementById("clear-username-btn");
    if (clearUsernameBtn) {
      clearUsernameBtn.addEventListener("click", clearUsernameAndRefresh);
    }


    // Final loading steps
    await delay(300);
    updateLoadingProgress(100, "Ready to play!");

    // Hide loading screen after showing 100%
    await delay(800);
    hideLoadingScreen();

    // Final render to ensure everything is displayed correctly after loading screen is hidden
    render();
  } // End initializeGame function

  // ===== CHECK FOR EXISTING USER DATA =====
  const existingUserData = getStoredUserData();

  if (existingUserData && existingUserData.username && existingUserData.email) {
    // Fetch fresh user score from API instead of using stored score
    try {
      const emailResult = await validateEmailAPI(existingUserData.email);

      if (
        emailResult.success &&
        !emailResult.isNewUser &&
        emailResult.userData
      ) {
        await initializeGameWithUser(
          existingUserData.username,
          existingUserData.email,
          emailResult.userData.score
        );
      } else {
        const initialScore = existingUserData.userData
          ? existingUserData.userData.score
          : 0;
        await initializeGameWithUser(
          existingUserData.username,
          existingUserData.email,
          initialScore
        );
      }
    } catch (error) {
      const initialScore = existingUserData.userData
        ? existingUserData.userData.score
        : 0;
      await initializeGameWithUser(
        existingUserData.username,
        existingUserData.email,
        initialScore
      );
    }
  } else {
    // Hide loading screen first, then show username screen
    hideLoadingScreen();
    await delay(300); // Brief delay for smooth transition
    showUsernameScreen();
  }

  // ===== DEBUG HELPER FUNCTIONS =====
  // Add global debug functions for console use
  window.debugGame = {
    getCurrentUser: () => {
      return window.currentUser || null;
    },

    testLeaderboard: async () => {
      const endpoint =
        "https://s4o7oxbjokq26tfociiy4qctay0umtzi.lambda-url.us-east-2.on.aws/";
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          mode: "cors",
          credentials: "omit",
          body: JSON.stringify({
            email: window.currentUser?.email || null,
            username: window.currentUser?.username || null,
          }),
        });
        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Test leaderboard error:', error);
        return null;
      }
    },

    showAllPlayers: async () => {
      const data = await window.debugGame.testLeaderboard();
      if (data && data.data && data.data.allPlayers) {
        console.table(data.data.allPlayers);
      }
    },

    loadLeaderboard: loadLeaderboardData,
  };

  // Make functions globally accessible for debugging
  window.loadLeaderboardData = loadLeaderboardData;
  window.showLeaderboard = showLeaderboard;
  window.showGamePanel = showGamePanel;

  // Add a simple test function for the leaderboard button
  window.testLeaderboardButton = function () {
    const btn = document.getElementById("btn");
    if (btn) {
      btn.click();
    }
  };

  // Add a test function for the back button
  window.testBackButton = function () {
    const backBtn = document.getElementById("back-to-game");
    if (backBtn) {
      backBtn.click();
    }
  };
}); // End DOMContentLoaded
